import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';
import type { Contact } from '../models/Contact';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import RHFInput from './RHF/RHFInput';
import type { EventType } from '../models/EventType';
import type { Event } from '../models/Event';
import { RHFSubmitButton } from './RHF/RHFSubmitButton';
import { numberOrNull } from '../helpers';
import { RHFSelect } from './RHF/RHFSelect';
import EventApi from '../apis/event_api';
import { handleError } from '../apis';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';
import { eventKeys } from '../queries/EventQueries';

const validator_event = z
  .object({
    applicationUserId: z.string().min(1, 'Required!'),
    eventId: z.number(),
    eventTypeId: z.number('Required!').min(1, 'Required!'),
    contactId: z.number('Required!'),
    date: z.string().min(1, 'Required!'),
    medication: z.string().trim().optional(),
    otherEventName: z.string().trim().optional(),
  })
  .refine(
    (data) => {
      if (data.eventTypeId === 2) {
        return !!data.medication;
      }
      return true;
    },
    {
      message: 'Required!',
      path: ['medication'],
    },
  )
  .refine(
    (data) => {
      if (data.eventTypeId === 7) {
        return !!data.otherEventName;
      }
      return true;
    },
    {
      message: 'Required!',
      path: ['otherEventName'],
    },
  );

export default function AddUpdateEventForm({
  eventTypes,
  contacts,

  event,
}: {
  eventTypes: EventType[];
  contacts: Contact[];
  event: Event;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    register,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      ...event,
      date: event.date ? format(new Date(event.date), 'yyyy-MM-dd') : undefined,
    },
    resolver: zodResolver(validator_event),
  });

  const typeId = useWatch({ control, name: 'eventTypeId' });

  const onHandleSubmit = async (values: Event) => {
    const toUpdate: Event = {
      eventId: values.eventId ? values.eventId : 0,
      applicationUserId: values.applicationUserId
        ? values.applicationUserId
        : '',
      medication: values.medication ? values.medication : '',
      date: values.date ? values.date : '',
      eventTypeId: values.eventTypeId ? values.eventTypeId : 0,
      contactId: values.contactId ? values.contactId : 0,
      otherEventName: values.otherEventName ? values.otherEventName : '',
    };
    let res: Event;
    try {
      if (!toUpdate.eventId) {
        res = await EventApi.createEvent(toUpdate);
      } else {
        res = await EventApi.updateEvent(toUpdate.eventId, toUpdate);
      }
      queryClient.invalidateQueries({
        queryKey: eventKeys.all,
      });

      return navigate(`/events/${res.eventId}`);
    } catch (error) {
      console.log(error);
      const message = handleError(error);
      toast.error(message);
    }
  };

  return (
    <form onSubmit={handleSubmit(onHandleSubmit)}>
      <h3>Event Details</h3>
      <div className='flex flex-wrap items-center justify-around gap-2'>
        <RHFInput
          label='Date'
          type='date'
          name='date'
          className='min-w-[275px]'
          register={register('date')}
          errors={errors}
        />
        <RHFSelect
          name='eventTypeId'
          label='Event Type'
          options={eventTypes}
          optionMethod={(eventTypes: EventType[]) =>
            eventTypes.map((g) => (
              <option value={g.eventTypeId} key={g.eventTypeId}>
                {g.eventTypeName}
              </option>
            ))
          }
          register={register('eventTypeId', {
            setValueAs: numberOrNull,
          })}
          errors={errors}
          className='min-w-[275px]'
        />
        {typeId == 7 && (
          <RHFInput
            name='otherEventName'
            label='Event Name'
            type='text'
            className='min-w-[275px]'
            register={register('otherEventName')}
            errors={errors}
          />
        )}
        {typeId == 2 && (
          <RHFInput
            name='medication'
            label='Medication/Dosage'
            type='text'
            className='min-w-[275px]'
            register={register('medication')}
            errors={errors}
          />
        )}
        <RHFSelect
          name='contactId'
          label='Performed By'
          options={contacts}
          optionMethod={(contacts: Contact[]) =>
            contacts.map((g) => (
              <option value={g.contactId} key={g.contactId}>
                {g.name}
              </option>
            ))
          }
          className='min-w-[275px]'
          register={register('contactId', {
            setValueAs: numberOrNull,
          })}
          errors={errors}
        />
      </div>

      <div className='mt-8 flex flex-wrap items-center justify-around'>
        <button
          type='button'
          onClick={() => navigate('/events')}
          className='my-2 w-full max-w-[275px] rounded-md border border-slate-950 bg-transparent p-2 text-xs font-bold uppercase hover:border-gray-400 hover:text-gray-400 dark:border-gray-500 dark:hover:border-gray-300 dark:hover:text-gray-300'
        >
          Cancel
        </button>
        <RHFSubmitButton label='Next' isSubmitting={isSubmitting} />
      </div>
    </form>
  );
}
